import {
  useEffect,
  useState
} from "react";

import {
  useQuery
} from "@tanstack/react-query";

import {
  ArrowRight,
  Check,
  Heart,
  Leaf,
  PackageCheck,
  Play,
  Sparkles,
  X
} from "lucide-react";

import {
  Link
} from "react-router";

import {
  getMakingRini,
  getMakingRiniMediaUrl
} from "../services/catalog";

function MakingRiniPageSkeleton() {
  return (
    <main>
      <section className="bg-[#edf1e8]">
        <div className="rini-container py-20 sm:py-24">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="h-4 w-40 animate-pulse rounded bg-black/10" />
              <div className="mt-5 h-20 max-w-xl animate-pulse rounded bg-black/10" />
              <div className="mt-6 h-24 max-w-xl animate-pulse rounded bg-black/10" />
            </div>

            <div className="aspect-[4/3] animate-pulse rounded-[2.5rem] bg-black/10" />
          </div>
        </div>
      </section>
    </main>
  );
}

export function MakingRiniPage() {
  const makingQuery =
    useQuery({
      queryKey: [
        "storefront",
        "making-rini"
      ],

      queryFn:
        getMakingRini,

      staleTime:
        60 * 1000
    });

  const [
    videoOpen,
    setVideoOpen
  ] =
    useState(false);

  const content =
    makingQuery.data;

  const posterUrl =
    content?.posterKey
      ? getMakingRiniMediaUrl(
          content.id,
          "poster",
          content.updatedAt
        )
      : null;

  const videoUrl =
    content?.videoKey
      ? getMakingRiniMediaUrl(
          content.id,
          "video",
          content.updatedAt
        )
      : null;

  const steps =
    content?.steps
      ?.slice()
      .sort(
        (left, right) =>
          left.displayOrder -
          right.displayOrder
      ) ??
    [];

  useEffect(() => {
    if (!videoOpen) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleEscape(
      event: KeyboardEvent
    ) {
      if (
        event.key ===
        "Escape"
      ) {
        setVideoOpen(false);
      }
    }

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [videoOpen]);

  if (makingQuery.isLoading) {
    return (
      <MakingRiniPageSkeleton />
    );
  }

  if (
    makingQuery.isError ||
    !content
  ) {
    return (
      <main className="rini-section">
        <div className="rini-container text-center">
          <h1 className="rini-heading text-5xl">
            Making RiNi
          </h1>

          <p className="mx-auto mt-5 max-w-xl leading-7 text-[#746d65]">
            Our making journey is being prepared.
            Please check back soon.
          </p>

          <Link
            to="/shop"
            className="rini-button-primary mt-8"
          >
            Explore RiNi
          </Link>
        </div>
      </main>
    );
  }

  function openVideo() {
    if (videoUrl) {
      setVideoOpen(true);
    }
  }

  return (
    <>
      <main>
        {/* ================================================= */}
        {/* HERO */}
        {/* ================================================= */}

        <section className="overflow-hidden bg-[#edf1e8]">
          <div className="rini-container py-16 sm:py-20 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#70845b]">
                  Behind the craft
                </p>

                <h1 className="rini-heading mt-5 text-5xl font-semibold leading-tight sm:text-6xl lg:text-7xl">
                  Making RiNi
                </h1>

                <p className="mt-6 max-w-xl text-lg leading-8 text-[#746d65]">
                  From thoughtfully selected ingredients
                  to careful preparation and packaging,
                  discover the journey behind every
                  RiNi product.
                </p>

                <div className="mt-8 flex flex-wrap gap-4">
                  {videoUrl && (
                    <button
                      type="button"
                      onClick={openVideo}
                      className="rini-button-primary"
                    >
                      <Play
                        size={18}
                        fill="currentColor"
                      />

                      {content.ctaText ??
                        "Watch our process"}
                    </button>
                  )}

                  <Link
                    to="/shop"
                    className="rini-button-secondary"
                  >
                    Explore products

                    <ArrowRight
                      size={18}
                    />
                  </Link>
                </div>
              </div>

              <button
                type="button"
                disabled={!videoUrl}
                onClick={openVideo}
                className={[
                  "group relative block aspect-[4/3] w-full overflow-hidden rounded-[2.5rem] bg-[#d8dfd2] text-left shadow-sm",
                  videoUrl
                    ? "cursor-pointer"
                    : "cursor-default"
                ].join(" ")}
                aria-label={
                  videoUrl
                    ? "Play Making RiNi video"
                    : "Making RiNi"
                }
              >
                {posterUrl ? (
                  <img
                    src={posterUrl}
                    alt="Making RiNi"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#82936f] to-[#4f6142]">
                    <span className="font-serif text-6xl text-white">
                      RiNi
                    </span>
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />

                {videoUrl && (
                  <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#5e3b2c] shadow-xl transition duration-300 group-hover:scale-110">
                    <Play
                      size={28}
                      fill="currentColor"
                    />
                  </span>
                )}

                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-serif text-2xl text-white">
                    Crafted in thoughtful batches
                  </p>

                  <p className="mt-1 text-sm text-white/80">
                    Follow the journey from preparation to packing.
                  </p>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* ================================================= */}
        {/* PHILOSOPHY */}
        {/* ================================================= */}

        <section className="rini-section bg-[#fffdf8]">
          <div className="rini-container">
            <div className="mx-auto max-w-4xl text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
                <Heart
                  size={24}
                  strokeWidth={1.5}
                />
              </span>

              <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Our philosophy
              </p>

              <h2 className="rini-heading mt-4 text-4xl font-semibold leading-tight sm:text-5xl">
                Thoughtful preparation,
                from beginning to finish
              </h2>

              <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[#746d65]">
                RiNi is built around thoughtful ingredients,
                carefully controlled preparation and attention
                to every small detail before a product reaches
                your self-care shelf.
              </p>
            </div>
          </div>
        </section>

        {/* ================================================= */}
        {/* PROCESS */}
        {/* ================================================= */}

        <section className="rini-section bg-[#f5efe7]">
          <div className="rini-container">
            <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                  The RiNi process
                </p>

                <h2 className="rini-heading mt-4 max-w-2xl text-4xl font-semibold sm:text-5xl">
                  Every step matters
                </h2>
              </div>

              <p className="max-w-xl leading-7 text-[#746d65]">
                A simple, considered process helps us focus
                on consistency, care and the finishing details
                that make each RiNi product special.
              </p>
            </div>

            {steps.length > 0 ? (
              <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {steps.map(
                  (
                    step,
                    index
                  ) => (
                    <article
                      key={step.id}
                      className="rounded-[2rem] border border-[#5e3b2c]/10 bg-white p-7"
                    >
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#5e3b2c] text-sm font-bold text-white">
                        {String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        )}
                      </span>

                      <h3 className="mt-6 font-serif text-2xl leading-tight text-[#38251e]">
                        {step.title}
                      </h3>

                      <div className="mt-6 h-px bg-[#5e3b2c]/10" />

                      <p className="mt-4 text-sm leading-6 text-[#746d65]">
                        Part of the RiNi preparation
                        and quality journey.
                      </p>
                    </article>
                  )
                )}
              </div>
            ) : (
              <div className="mt-12 rounded-[2rem] bg-white p-10 text-center text-[#746d65]">
                Process details will be added soon.
              </div>
            )}
          </div>
        </section>

        {/* ================================================= */}
        {/* FEATURE VIDEO */}
        {/* ================================================= */}

        {videoUrl && (
          <section className="rini-section bg-[#38251e] text-white">
            <div className="rini-container">
              <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#d6c4ad]">
                    Watch the journey
                  </p>

                  <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight sm:text-5xl">
                    See how RiNi comes together
                  </h2>

                  <p className="mt-5 max-w-xl text-lg leading-8 text-white/70">
                    Take a closer look at the preparation,
                    handling and packaging behind the
                    RiNi collection.
                  </p>

                  <button
                    type="button"
                    onClick={openVideo}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-semibold text-[#38251e]"
                  >
                    <Play
                      size={17}
                      fill="currentColor"
                    />

                    Play full video
                  </button>
                </div>

                <button
                  type="button"
                  onClick={openVideo}
                  className="group relative aspect-video overflow-hidden rounded-[2rem] bg-black"
                  aria-label="Play Making RiNi video"
                >
                  {posterUrl && (
                    <img
                      src={posterUrl}
                      alt="RiNi making process"
                      loading="lazy"
                      className="h-full w-full object-cover opacity-80 transition duration-500 group-hover:scale-[1.02]"
                    />
                  )}

                  <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#5e3b2c] shadow-xl transition group-hover:scale-110">
                    <Play
                      size={28}
                      fill="currentColor"
                    />
                  </span>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ================================================= */}
        {/* VALUES */}
        {/* ================================================= */}

        <section className="rini-section bg-[#fffdf8]">
          <div className="rini-container">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Why the process matters
              </p>

              <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
                Care you can see
              </h2>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              <article className="rounded-[2rem] bg-[#edf1e8] p-8">
                <Leaf
                  size={29}
                  className="text-[#70845b]"
                  strokeWidth={1.5}
                />

                <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                  Thoughtful ingredients
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Botanicals and formulation choices are
                  considered as part of the complete
                  self-care experience.
                </p>
              </article>

              <article className="rounded-[2rem] bg-[#f3e7d9] p-8">
                <Sparkles
                  size={29}
                  className="text-[#a27a4d]"
                  strokeWidth={1.5}
                />

                <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                  Small-batch attention
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Controlled preparation allows attention
                  to texture, consistency and finishing.
                </p>
              </article>

              <article className="rounded-[2rem] bg-[#eee7df] p-8">
                <PackageCheck
                  size={29}
                  className="text-[#6f5140]"
                  strokeWidth={1.5}
                />

                <h3 className="mt-6 font-serif text-2xl text-[#38251e]">
                  Carefully packed
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  Products are checked and thoughtfully
                  prepared before completing their journey
                  to you.
                </p>
              </article>
            </div>

            <div className="mt-10 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#70845b]/20 bg-[#f8faf6] px-5 py-3 text-sm font-medium text-[#526746]">
                <Check size={17} />
                Thoughtfully made. Carefully finished.
              </div>
            </div>
          </div>
        </section>

        {/* ================================================= */}
        {/* FINAL CTA */}
        {/* ================================================= */}

        <section className="bg-[#efe3d2]">
          <div className="rini-container py-16 text-center sm:py-20">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              Experience RiNi
            </p>

            <h2 className="rini-heading mx-auto mt-4 max-w-3xl text-4xl font-semibold sm:text-5xl">
              From our making journey to your everyday ritual
            </h2>

            <p className="mx-auto mt-5 max-w-2xl leading-8 text-[#746d65]">
              Explore RiNi bodycare or discover thoughtfully
              curated gift hampers made for meaningful moments.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/shop"
                className="rini-button-primary"
              >
                Shop RiNi

                <ArrowRight
                  size={18}
                />
              </Link>

              <Link
                to="/gift-hampers"
                className="rini-button-secondary"
              >
                Explore gift hampers
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* =================================================== */}
      {/* VIDEO MODAL */}
      {/* =================================================== */}

      {videoOpen &&
        videoUrl && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Making RiNi video"
            onMouseDown={(
              event
            ) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setVideoOpen(false);
              }
            }}
          >
            <div className="relative w-full max-w-5xl">
              <button
                type="button"
                onClick={() =>
                  setVideoOpen(false)
                }
                className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#38251e]"
                aria-label="Close video"
              >
                <X size={21} />
              </button>

              <video
                controls
                autoPlay
                preload="metadata"
                playsInline
                poster={
                  posterUrl ??
                  undefined
                }
                className="max-h-[82vh] w-full rounded-2xl bg-black shadow-2xl"
              >
                <source
                  src={videoUrl}
                />

                Your browser does not support HTML5 video.
              </video>
            </div>
          </div>
        )}
    </>
  );
}
